import React from 'react';
import { X, User, Mail, Phone, Calendar, Briefcase, FileText, Download } from 'lucide-react';
import { BASE_URL } from '../utils/BASE_URL';

const ViewCareerModal = ({ isOpen, onClose, application }) => {
    if (!isOpen || !application) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white rounded-xl shadow-2xl w-[95%] max-w-4xl overflow-hidden animate-fade-in-up flex flex-col max-h-[90vh]">

                {/* Header */}
                <div className="flex justify-between items-center p-4 md:p-6 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                    <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-blue-600" />
                        Application Details
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-200 rounded-full transition-colors text-gray-500 hover:text-gray-700"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="p-4 md:p-8 space-y-6 overflow-y-auto custom-scrollbar">
                    {/* Basic Info Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                                <User className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Candidate Name</p>
                                <p className="text-gray-800 font-medium text-lg">{application.fullname}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                                <Mail className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Email Address</p>
                                <p className="text-gray-800 font-medium">{application.email}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-green-50 text-green-600 rounded-lg">
                                <Phone className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Phone Number</p>
                                <p className="text-gray-800 font-medium">{application.phone}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-orange-50 text-orange-600 rounded-lg">
                                <Briefcase className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Position Applied</p>
                                <p className="text-gray-800 font-medium">{application.position}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-pink-50 text-pink-600 rounded-lg">
                                <Calendar className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Experience</p>
                                <p className="text-gray-800 font-medium">{application.experience}</p>
                            </div>
                        </div>

                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-gray-50 text-gray-600 rounded-lg">
                                <Calendar className="w-5 h-5" />
                            </div>
                            <div>
                                <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Application Date</p>
                                <p className="text-gray-800 font-medium">
                                    {new Date(application.created_at).toLocaleDateString('en-GB', {
                                        day: 'numeric',
                                        month: 'short',
                                        year: 'numeric'
                                    })}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Cover Letter */}
                    {application.cover_latter && (
                        <div className="bg-gray-50 p-6 rounded-xl border border-gray-100">
                            <div className="flex items-center gap-2 mb-3 text-gray-500">
                                <FileText className="w-4 h-4" />
                                <h4 className="text-sm font-semibold uppercase tracking-wider">Cover Letter</h4>
                            </div>
                            <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-base">
                                {application.cover_latter}
                            </p>
                        </div>
                    )}

                    {/* Attachments Section */}
                    {application.cv && application.cv.length > 0 && (
                        <div>
                            <h4 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <Download className="w-5 h-5 text-blue-600" />
                                Attachments ({application.cv.length})
                            </h4>
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                {application.cv.map((file, index) => {
                                    const isImage = ['jpg', 'jpeg', 'png', 'webp', 'gif'].includes(file.file_type?.toLowerCase()) ||
                                        file.url.match(/\.(jpg|jpeg|png|webp|gif)$/i);

                                    return (
                                        <div key={index} className="border border-gray-200 rounded-lg p-3 hover:shadow-md transition-shadow bg-gray-50 group relative">
                                            {isImage ? (
                                                <div className="aspect-video w-full overflow-hidden rounded-md mb-2 bg-gray-200 relative">
                                                    <img
                                                        src={file.url}
                                                        alt={`Attachment ${index + 1}`}
                                                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                                    />
                                                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                                        <a
                                                            href={file.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            className="text-white bg-black/50 p-2 rounded-full hover:bg-black/70"
                                                        >
                                                            <Download className="w-5 h-5" />
                                                        </a>
                                                    </div>
                                                </div>
                                            ) : (
                                                <div className="aspect-video w-full bg-white rounded-md mb-2 flex flex-col items-center justify-center border border-gray-100 text-gray-400">
                                                    <FileText className="w-12 h-12 mb-2" />
                                                    <span className="text-xs font-mono uppercase bg-gray-100 px-2 py-0.5 rounded">{file.file_type || 'DOC'}</span>
                                                </div>
                                            )}

                                            <div className="flex items-center justify-between mt-2">
                                                <span className="text-xs font-medium text-gray-600 truncate max-w-[150px]">
                                                    Attachment {index + 1}
                                                </span>
                                                <a
                                                    href={file.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="text-xs text-blue-600 hover:text-blue-800 font-medium flex items-center gap-1"
                                                >
                                                    View / Download
                                                </a>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ViewCareerModal;
